import DragHandleRounded from '@mui/icons-material/DragHandleRounded';
import { Box, useTheme } from '@mui/material';
import { MouseEventHandler, useEffect, useRef } from 'react';

import { useScreenOrientation } from '@hooks/useScreenOrientation';

import { DRAWER } from './config';
import { useStyles } from './hooks/useStyles';

interface ResizerProps {
  onChangeDrawerWidth: (value: number) => void;
  drawerWidth: number;
}

const Resizer = ({ onChangeDrawerWidth, drawerWidth }: ResizerProps) => {
  const theme = useTheme();
  const { getResizerStyles, getResizerIconStyles } = useStyles();
  const orientation = useScreenOrientation();
  const isResizing = useRef(false);

  const onMouseDown: MouseEventHandler<HTMLDivElement> = (event) => {
    isResizing.current = true;
    event.preventDefault();
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isResizing.current) return;
    const diff = (orientation === 'landscape' ? e.clientX : e.clientY) - drawerWidth;
    const nextSize = drawerWidth + diff;
    onChangeDrawerWidth(nextSize > DRAWER.maxWidth ? DRAWER.maxWidth : nextSize);
  };

  const onMouseUp = () => {
    isResizing.current = false;
  };

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orientation]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <Box
      onMouseDown={onMouseDown}
      sx={{
        position: 'absolute',
        ...(orientation === 'landscape' ? { right: 0 } : { bottom: 0 }),
        padding: '6px 0 0',
        zIndex: 100,
        bgcolor: theme.palette.grey[800],
        cursor: orientation === 'landscape' ? 'ew-resize' : 'ns-resize',
        ...getResizerStyles(),
      }}
    >
      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        <DragHandleRounded
          sx={{
            position: 'absolute',
            height: 20,
            width: 20,
            color: theme.palette.grey[600],
            ...getResizerIconStyles(),
          }}
        />
      </Box>
    </Box>
  );
};

interface ResponsiveDrawerProps {
  drawerWidth: number;
  onChangeDrawerWidth: (value: number) => void;
  content: React.ReactNode;
}

export const ResponsiveDrawer = ({ drawerWidth = 0, onChangeDrawerWidth, content }: ResponsiveDrawerProps) => {
  const { getDrawerStyles } = useStyles();
  const orientation = useScreenOrientation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: orientation === 'landscape' ? 'row' : 'column',
        backgroundColor: theme.palette.grey[800],
        ...getDrawerStyles(drawerWidth),
        mr: `${DRAWER.resizerWidth}px`,
      }}
    >
      {content}
      <Resizer onChangeDrawerWidth={onChangeDrawerWidth} drawerWidth={drawerWidth} />
    </Box>
  );
};
